import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { saveAs } from "file-saver";
import { DataGrid } from "@mui/x-data-grid";
import { supabase } from "./supabaseClient";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./ZReport.css";
import { createPortal } from "react-dom";

export default function Clockins() {
  // ----------------- AUTH / ROLE -----------------
  const [user, setUser] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [role, setRole] = useState("staff"); // default
  const [myEmployeeName, setMyEmployeeName] = useState(""); // logged-in staff full_name
  const isAdmin = role === "admin";

  const formatEditDate = (d) => (d ? format(new Date(d), "dd MMM yyyy") : "Unknown date");

  // ----------------- UK TIME HELPERS -----------------
  const getUKHHmm = () =>
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date());

  // accept optional date
  const getUKHHmmss = (d = new Date()) =>
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(d);

  const getUKTodayYMD = () => {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());

    const y = parts.find((p) => p.type === "year")?.value;
    const m = parts.find((p) => p.type === "month")?.value;
    const d = parts.find((p) => p.type === "day")?.value;
    return `${y}-${m}-${d}`;
  };

  const formatUKDateNice = (d = new Date()) =>
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(d);

  const isValidHHmm = (s) => /^\d{2}:\d{2}$/.test(String(s || ""));
  const cleanTime = (t) => (t ? String(t).substring(0, 5) : "");

  // ----------------- FORM STATE -----------------
  const [form, setForm] = useState({
    date: new Date(),
    employee: "",
    clockIn: "",
    clockOut: "",
    breakMinutes: 0,
    onShiftDeliveries: 0,
    offShiftDeliveries: 0,
    freeDeliveries: 0,
    totalDeliveries: 0,
    deliveryFee: "",
    totalHoursFormatted: "00:00",
    totalHoursDecimal: 0,
    notes: "",
  });

  const calculateHours = (clockIn, clockOut, breakMinutes) => {
    if (!clockIn || !clockOut) return { decimal: 0, formatted: "00:00" };

    const start = new Date(`1970-01-01T${clockIn}:00`);
    const end = new Date(`1970-01-01T${clockOut}:00`);

    if (end <= start) end.setDate(end.getDate() + 1);

    let diffMs = end - start - breakMinutes * 60000;
    if (diffMs < 0) diffMs = 0;

    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return {
      decimal: totalMinutes / 60,
      formatted: `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`,
    };
  };

  const handleChange = (field, value) => {
    setForm((prev) => {
      const updated = { ...prev, [field]: value };

      if (field === "onShiftDeliveries" || field === "offShiftDeliveries" || field === "freeDeliveries") {
        updated.totalDeliveries =
          Number(updated.onShiftDeliveries) + Number(updated.offShiftDeliveries) + Number(updated.freeDeliveries);
      }

      if (["clockIn", "clockOut", "breakMinutes"].includes(field)) {
        const { decimal, formatted } = calculateHours(
          cleanTime(updated.clockIn),
          cleanTime(updated.clockOut),
          Number(updated.breakMinutes)
        );
        updated.totalHoursDecimal = decimal;
        updated.totalHoursFormatted = formatted;
      }

      return updated;
    });
  };

  // ----------------- SHIFT TRACKING -----------------
  const [activeShiftId, setActiveShiftId] = useState(null);
  const [lastShiftId, setLastShiftId] = useState(null);

  // ✅ persisted break
  const [breakStartAt, setBreakStartAt] = useState(null);
  const [breakElapsed, setBreakElapsed] = useState(0);

  const [expectedClockOut, setExpectedClockOut] = useState("");

  // live clock
  const [ukNow, setUkNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setUkNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // running total while shift open
  const [runningTotal, setRunningTotal] = useState("00:00");
  useEffect(() => {
    if (!form.clockIn || form.clockOut) {
      setRunningTotal("00:00");
      return;
    }
    const tick = () => {
      const now = getUKHHmm();
      const { formatted } = calculateHours(cleanTime(form.clockIn), now, Number(form.breakMinutes || 0));
      setRunningTotal(formatted);
    };
    tick();
    const i = setInterval(tick, 30 * 1000);
    return () => clearInterval(i);
    // eslint-disable-next-line
  }, [form.clockIn, form.clockOut, form.breakMinutes]);

  // ✅ live break timer display
  useEffect(() => {
    if (!breakStartAt) {
      setBreakElapsed(0);
      return;
    }
    const tick = () => {
      const mins = Math.floor((new Date() - breakStartAt) / 60000);
      setBreakElapsed(mins > 0 ? mins : 0);
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [breakStartAt]);

  // ----------------- EMPLOYEES -----------------
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    async function fetchEmployees() {
      const { data, error } = await supabase.from("employees").select("full_name").order("full_name", { ascending: true });
      if (!error && data) setEmployees(data.map((e) => e.full_name));
    }
    fetchEmployees();
  }, []);

  // ----------------- TABLE -----------------
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [editId, setEditId] = useState(null);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filterEmployee, setFilterEmployee] = useState("");

  // ----------------- AUTH LOAD -----------------
  useEffect(() => {
    let mounted = true;

    const loadUserAndRole = async () => {
      const { data } = await supabase.auth.getUser();
      const u = data?.user ?? null;

      if (!mounted) return;
      setUser(u);
      setAuthUser(u);

      const email = u?.email;
      if (!email) {
        setRole("staff");
        setMyEmployeeName("");
        return;
      }

      const { data: emp, error } = await supabase
        .from("employees")
        .select("role, full_name")
        .eq("email", email)
        .single();

      if (error || !emp) {
        setRole("staff");
        setMyEmployeeName("");
      } else {
        setRole(String(emp.role || "staff").toLowerCase());
        setMyEmployeeName(emp.full_name || "");
      }
    };

    loadUserAndRole();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setAuthUser(session?.user ?? null);
      setTimeout(loadUserAndRole, 0);
    });

    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  // ----------------- FETCH CLOCKINS -----------------
  const fetchClockins = async (start, end, emp) => {
    setTableLoading(true);

    let q = supabase.from("clockins").select("*").order("date", { ascending: false });

    if (start && end) {
      q = q.gte("date", start).lte("date", end);
    } else {
      const today = new Date();
      const twoDaysAgo = new Date();
      twoDaysAgo.setDate(today.getDate() - 2);
      q = q.gte("date", format(twoDaysAgo, "yyyy-MM-dd"));
    }

    if (emp) q = q.eq("employee", emp);

    const { data, error } = await q;

    if (!error && data) {
      setRows(
        data.map((item) => ({
          id: item.id,
          date: format(new Date(item.date), "dd/MM/yyyy"),
          employee: item.employee,
          clock_in: item.clock_in,
          clock_out: item.clock_out || "⏳ In progress",
          on_shift: item.on_shift_deliveries,
          off_shift: item.off_shift_deliveries,
          free: item.free_deliveries,
          total: item.total_deliveries,
          delivery_fee: `£${(item.delivery_fee ?? 0).toFixed(2)}`,
          break_minutes: item.break_minutes,
          total_hours: item.total_hours_formatted,
          auto_closed: typeof item.auto_clocked_out === "boolean" ? (item.auto_clocked_out ? "Yes" : "No") : "N/A",
          notes: item.notes || "",
          logged_by: item.logged_by,
          logged_at: item.logged_at ? format(new Date(item.logged_at), "dd/MM/yyyy HH:mm") : "N/A",
        }))
      );
    }

    setTableLoading(false);
  };

  useEffect(() => {
    fetchClockins();
    // eslint-disable-next-line
  }, []);

  // ----------------- EDIT MODE HELPERS -----------------
  const exitEditMode = (message = "Edit cancelled.") => {
    setEditId(null);
    setForm((prev) => ({
      ...prev,
      clockIn: "",
      clockOut: "",
      breakMinutes: 0,
      onShiftDeliveries: 0,
      offShiftDeliveries: 0,
      freeDeliveries: 0,
      totalDeliveries: 0,
      deliveryFee: "",
      notes: prev.notes || "",
      totalHoursFormatted: "00:00",
      totalHoursDecimal: 0,
    }));
    toast.info(message);
  };

  // ----------------- FIND OPEN SHIFT -----------------
  const findOpenShiftForToday = async (employeeName) => {
    if (!employeeName) return null;

    const today = getUKTodayYMD();
    const { data, error } = await supabase
      .from("clockins")
      .select("id, clock_in, break_minutes, notes, expected_clock_out, break_active, break_started_at")
      .eq("employee", employeeName)
      .eq("date", today)
      .is("clock_out", null)
      .order("id", { ascending: false })
      .limit(1);

    if (error) {
      toast.error("Error checking open shift: " + error.message);
      return null;
    }

    return data?.[0] ?? null;
  };

  // ----------------- EXPECTED CLOCK OUT MODAL -----------------
  const [expectedModalOpen, setExpectedModalOpen] = useState(false);
  const [expectedInput, setExpectedInput] = useState("");
  const [pendingClockInTime, setPendingClockInTime] = useState("");

  // ----------------- SNAPPY EDIT MODE -----------------
  const [snappyEditId, setSnappyEditId] = useState(null);
  const isSnappyEditing = !!snappyEditId && !editId;

  const canSnappyEditRow = (row) => {
    if (!row) return false;
    if (isAdmin) return true;
    return row.employee && myEmployeeName && row.employee === myEmployeeName;
  };

  const exitSnappyEditMode = (message = "Snappy edit cancelled.") => {
    setSnappyEditId(null);
    setForm((prev) => ({
      ...prev,
      onShiftDeliveries: 0,
      offShiftDeliveries: 0,
      freeDeliveries: 0,
      totalDeliveries: 0,
      deliveryFee: "",
      notes: "",
    }));
    toast.info(message);
  };

  const handleSnappyEdit = (id) => {
    const r = rows.find((x) => x.id === id);
    if (!r) return;

    if (!canSnappyEditRow(r)) {
      return toast.error("❌ You can only Snappy Edit your own entries.");
    }

    if (editId) exitEditMode("Admin edit cleared.");
    setSnappyEditId(id);

    // stop shift controls while editing a past row
    setActiveShiftId(null);
    setLastShiftId(id);

    // ✅ if you are snappy-editing, do not show break state
    setBreakStartAt(null);
    setExpectedClockOut("");

    setForm((prev) => ({
      ...prev,
      date: new Date(r.date.split("/").reverse().join("-")),
      employee: r.employee,
      clockIn: cleanTime(r.clock_in),
      clockOut: r.clock_out === "⏳ In progress" ? "" : cleanTime(r.clock_out),
      breakMinutes: Number(r.break_minutes || 0),
      onShiftDeliveries: Number(r.on_shift || 0),
      offShiftDeliveries: Number(r.off_shift || 0),
      freeDeliveries: Number(r.free || 0),
      totalDeliveries: Number(r.total || 0),
      deliveryFee: String(r.delivery_fee || "£0").replace("£", ""),
      notes: r.notes || "",
    }));

    toast.info("🟦 Snappy Edit loaded. Only deliveries/fee/notes can be changed.");
  };

  // ----------------- EMPLOYEE SELECT -----------------
  const handleEmployeeSelect = async (emp) => {
    setExpectedModalOpen(false);

    if (editId) exitEditMode("Edit cleared.");
    if (snappyEditId) exitSnappyEditMode("Snappy edit cleared.");

    // ✅ clear break UI immediately when switching employee
    setBreakStartAt(null);
    setBreakElapsed(0);

    handleChange("employee", emp);

    setActiveShiftId(null);
    setLastShiftId(null);
    setExpectedClockOut("");

    handleChange("date", new Date(getUKTodayYMD()));
    handleChange("clockIn", "");
    handleChange("clockOut", "");
    handleChange("notes", "");
    handleChange("breakMinutes", 0);

    if (!emp) return;

    const open = await findOpenShiftForToday(emp);
    if (open) {
      setActiveShiftId(open.id);
      setLastShiftId(open.id);

      handleChange("clockIn", cleanTime(open.clock_in));
      handleChange("breakMinutes", Number(open.break_minutes || 0));
      handleChange("notes", open.notes || "");

      setExpectedClockOut(open.expected_clock_out ? cleanTime(open.expected_clock_out) : "");

      // ✅ restore break
      if (open.break_active && open.break_started_at) {
        setBreakStartAt(new Date(open.break_started_at));
        toast.info("⏸️ Break is currently active for this shift.");
      } else {
        setBreakStartAt(null);
      }

      toast.info("⏳ Open shift found for today. Please Clock Out when finished.");
    }
  };

  // ----------------- ADMIN EDIT/DELETE -----------------
  const handleEdit = (id) => {
    if (!isAdmin) return toast.error("Admins only.");

    const r = rows.find((x) => x.id === id);
    if (!r) return;

    const { decimal, formatted } = calculateHours(
      cleanTime(r.clock_in),
      r.clock_out === "⏳ In progress" ? "" : cleanTime(r.clock_out),
      Number(r.break_minutes || 0)
    );

    setEditId(id);
    setSnappyEditId(null);
    setActiveShiftId(null);
    setLastShiftId(id);

    // ✅ admin edit doesn't use break controls
    setBreakStartAt(null);
    setExpectedClockOut("");

    setForm((prev) => ({
      ...prev,
      date: new Date(r.date.split("/").reverse().join("-")),
      employee: r.employee,
      clockIn: cleanTime(r.clock_in),
      clockOut: r.clock_out === "⏳ In progress" ? "" : cleanTime(r.clock_out),
      breakMinutes: Number(r.break_minutes || 0),
      onShiftDeliveries: Number(r.on_shift || 0),
      offShiftDeliveries: Number(r.off_shift || 0),
      freeDeliveries: Number(r.free || 0),
      totalDeliveries: Number(r.total || 0),
      deliveryFee: String(r.delivery_fee || "£0").replace("£", ""),
      totalHoursFormatted: formatted,
      totalHoursDecimal: decimal,
      notes: r.notes || "",
    }));

    toast.info("Admin edit loaded. Adjust times then click Save Edit.");
  };

  const handleDelete = async (id) => {
    if (!isAdmin) return toast.error("Admins only.");
    if (editId === id) exitEditMode("Deleted edited row. Edit closed.");
    if (snappyEditId === id) exitSnappyEditMode("Deleted row. Snappy edit closed.");

    const ok = prompt("Type DELETE to confirm:");
    if (ok !== "DELETE") return toast.info("Delete cancelled.");

    setLoading(true);
    const { error } = await supabase.from("clockins").delete().eq("id", id);
    setLoading(false);

    if (error) return toast.error("Error deleting: " + error.message);

    toast.success("Entry deleted.");
    fetchClockins(fromDate, toDate, filterEmployee);
  };

  // ----------------- CLOCK IN (EXPECTED MODAL) -----------------
  const openExpectedModalForClockIn = () => {
    if (!form.employee) return toast.error("❌ Please select employee first.");
    if (activeShiftId) return toast.info("⏳ You are already clocked in. Please Clock Out first.");
    if (isSnappyEditing) return toast.info("❌ Exit Snappy Edit first.");

    const today = getUKTodayYMD();
    const nowTime = getUKHHmm();

    handleChange("date", new Date(today));
    handleChange("clockIn", nowTime);
    handleChange("clockOut", "");
    handleChange("breakMinutes", 0);

    // no break on clock in
    setBreakStartAt(null);
    setBreakElapsed(0);

    setPendingClockInTime(nowTime);
    setExpectedInput("");
    setExpectedModalOpen(true);
  };

  const cancelExpectedModal = () => {
    if (loading) return;
    setExpectedModalOpen(false);

    handleChange("clockIn", "");
    setPendingClockInTime("");
    setExpectedInput("");
  };

  const confirmClockInAndInsert = async () => {
    if (!isValidHHmm(expectedInput)) {
      toast.error("❌ Please enter expected clock-out in HH:MM format (e.g. 14:00).");
      return;
    }

    const today = getUKTodayYMD();
    const nowTime = pendingClockInTime || getUKHHmm();

    const inT = new Date(`1970-01-01T${nowTime}:00`);
    const expT = new Date(`1970-01-01T${expectedInput}:00`);
    if (expT <= inT) {
      toast.error("❌ Expected clock-out must be after clock-in.");
      return;
    }

    const checkAt = new Date(`${today}T${expectedInput}:00`);
    checkAt.setMinutes(checkAt.getMinutes() + 15);

    setLoading(true);

    const {
      data: { user: au },
    } = await supabase.auth.getUser();

    const displayName = au?.user_metadata?.display_name || au?.email || "Manual Entry";

    const payload = {
      date: today,
      employee: form.employee,
      clock_in: `${nowTime}:00`,
      clock_out: null,
      break_minutes: 0,

      // ✅ break persisted fields
      break_active: false,
      break_started_at: null,

      on_shift_deliveries: 0,
      off_shift_deliveries: 0,
      free_deliveries: 0,
      total_deliveries: 0,
      delivery_fee: 0,
      total_hours_decimal: 0,
      total_hours_formatted: "00:00",
      notes: form.notes || "",
      logged_at: new Date().toISOString(),
      logged_by: displayName,

      expected_clock_out: `${expectedInput}:00`,
      auto_close_check_at: checkAt.toISOString(),
      auto_clocked_out: false,
    };

    const { data, error } = await supabase.from("clockins").insert([payload]).select("id").single();

    setLoading(false);

    if (error) {
      toast.error("Error clocking in: " + error.message);
      cancelExpectedModal();
      return;
    }

    setActiveShiftId(data.id);
    setLastShiftId(data.id);
    setExpectedClockOut(expectedInput);
    setExpectedModalOpen(false);

    toast.success(`✅ Clocked in at ${nowTime} (UK time). Expected out: ${expectedInput}`);
    fetchClockins(fromDate, toDate, filterEmployee);
  };

  // ----------------- BREAK BUTTONS (PERSISTED) -----------------
  const handleBreakStart = async () => {
    if (isSnappyEditing) return toast.error("❌ Exit Snappy Edit first.");
    if (!activeShiftId) return toast.error("❌ Clock In first.");
    if (form.clockOut) return toast.error("❌ Already clocked out.");
    if (breakStartAt) return toast.info("Break already started.");

    const startedAt = new Date();

    setLoading(true);

    // safety check: still open
    const { data: shift, error: shiftErr } = await supabase
      .from("clockins")
      .select("id, clock_out, employee, date")
      .eq("id", activeShiftId)
      .single();

    if (shiftErr || !shift) {
      setLoading(false);
      return toast.error("❌ Shift not found.");
    }
    if (shift.clock_out !== null) {
      setLoading(false);
      return toast.error("❌ This shift is already clocked out.");
    }

    const { error } = await supabase
      .from("clockins")
      .update({
        break_active: true,
        break_started_at: startedAt.toISOString(),
        logged_at: new Date().toISOString(),
      })
      .eq("id", activeShiftId)
      .is("clock_out", null);

    setLoading(false);

    if (error) return toast.error("Error starting break: " + error.message);

    setBreakStartAt(startedAt);
    toast.info("⏸️ Break started.");
    fetchClockins(fromDate, toDate, filterEmployee);
  };

  const handleBreakEnd = async () => {
    if (isSnappyEditing) return toast.error("❌ Exit Snappy Edit first.");
    if (!breakStartAt) return toast.error("❌ Break has not started.");
    if (!activeShiftId) return toast.error("❌ No active shift found.");
    if (form.clockOut) return toast.error("❌ Already clocked out.");

    const mins = Math.floor((new Date() - breakStartAt) / 60000);
    const add = Number.isFinite(mins) && mins > 0 ? mins : 0;

    const newBreak = Number(form.breakMinutes || 0) + add;

    setLoading(true);

    const { error } = await supabase
      .from("clockins")
      .update({
        break_minutes: newBreak,
        break_active: false,
        break_started_at: null,
        logged_at: new Date().toISOString(),
      })
      .eq("id", activeShiftId)
      .is("clock_out", null);

    setLoading(false);

    if (error) return toast.error("Error saving break: " + error.message);

    setBreakStartAt(null);
    setBreakElapsed(0);
    handleChange("breakMinutes", newBreak);

    toast.info(`▶️ Break ended (+${add} min).`);
    fetchClockins(fromDate, toDate, filterEmployee);
  };

  // ----------------- CLOCK OUT -----------------
  const handleClockOutNow = async () => {
    if (isSnappyEditing) return toast.error("❌ Exit Snappy Edit first.");
    if (!form.employee) return toast.error("❌ Please select employee first.");
    if (!activeShiftId) return toast.error("❌ No active shift found. Clock In first.");
    if (breakStartAt) return toast.error("❌ Please end your break before clocking out.");

    const today = getUKTodayYMD();
    const selected = format(form.date, "yyyy-MM-dd");
    if (selected !== today) {
      return toast.error("❌ You missed clock out yesterday. Ask admin to fix.");
    }

    const nowTime = getUKHHmm();

    const inT = new Date(`1970-01-01T${cleanTime(form.clockIn)}:00`);
    const outT = new Date(`1970-01-01T${nowTime}:00`);
    if (outT <= inT) {
      return toast.error("❌ Clock Out must be after Clock In.");
    }

    const MAX_SHIFT_HOURS = 12;
    const { decimal, formatted } = calculateHours(cleanTime(form.clockIn), nowTime, Number(form.breakMinutes || 0));
    if (decimal > MAX_SHIFT_HOURS) {
      return toast.error(`❌ Shift exceeds ${MAX_SHIFT_HOURS} hours. Ask admin to fix.`);
    }

    setLoading(true);

    // safety check
    const { data: shift, error: shiftErr } = await supabase
      .from("clockins")
      .select("id, employee, date, clock_out")
      .eq("id", activeShiftId)
      .single();

    if (shiftErr || !shift) {
      setLoading(false);
      return toast.error("❌ Shift not found.");
    }
    if (shift.clock_out !== null) {
      setLoading(false);
      return toast.error("❌ This shift is already clocked out.");
    }
    if (shift.employee !== form.employee || shift.date !== today) {
      setLoading(false);
      return toast.error(`❌ Safety stop: this shift belongs to ${shift.employee} (${shift.date}).`);
    }

    const { error } = await supabase
      .from("clockins")
      .update({
        clock_out: `${nowTime}:00`,
        break_minutes: Number(form.breakMinutes || 0),
        total_hours_decimal: decimal,
        total_hours_formatted: formatted,
        logged_at: new Date().toISOString(),
      })
      .eq("id", activeShiftId)
      .is("clock_out", null);

    setLoading(false);

    if (error) return toast.error("Error clocking out: " + error.message);

    handleChange("clockOut", nowTime);
    handleChange("totalHoursDecimal", decimal);
    handleChange("totalHoursFormatted", formatted);

    toast.success(`✅ Clocked out at ${nowTime} (UK time).`);

    setLastShiftId(activeShiftId);
    setActiveShiftId(null);

    // ensure break cleared
    setBreakStartAt(null);
    setBreakElapsed(0);

    fetchClockins(fromDate, toDate, filterEmployee);
  };

  // ----------------- SAVE DELIVERIES/NOTES -----------------
  const handleSaveDetails = async () => {
    if (!form.employee) return toast.error("❌ Please select employee first.");

    const targetId = editId || snappyEditId || activeShiftId || lastShiftId;
    if (!targetId) {
      return toast.error("❌ No shift selected. Clock In first (or select a row).");
    }

    // staff safety stop for snappy
    if (snappyEditId && !isAdmin) {
      if (!myEmployeeName) return toast.error("❌ Your employee profile is missing (full_name).");
      if (form.employee !== myEmployeeName) {
        return toast.error("❌ Safety stop: you can only save Snappy details for your own entries.");
      }
    }

    setLoading(true);

    const snappyPayload = {
      on_shift_deliveries: Number(form.onShiftDeliveries || 0),
      off_shift_deliveries: Number(form.offShiftDeliveries || 0),
      free_deliveries: Number(form.freeDeliveries || 0),
      total_deliveries: Number(form.totalDeliveries || 0),
      delivery_fee: parseFloat(form.deliveryFee || 0),
      notes: form.notes || "",
      logged_at: new Date().toISOString(),
    };

    // In Snappy Edit mode, DO NOT touch break minutes / clock fields
    const payload = snappyEditId && !editId ? snappyPayload : { ...snappyPayload, break_minutes: Number(form.breakMinutes || 0) };

    const { error } = await supabase.from("clockins").update(payload).eq("id", targetId);

    setLoading(false);

    if (error) return toast.error("Error saving: " + error.message);

    toast.success("✅ Saved.");
    fetchClockins(fromDate, toDate, filterEmployee);

    if (editId) setEditId(null);
    if (snappyEditId) setSnappyEditId(null);
  };

  // ----------------- ADMIN SAVE EDIT -----------------
  const handleSaveEdit = async () => {
    if (!editId) return toast.error("No row in edit mode.");
    if (!form.clockIn) return toast.error("Clock In is required.");

    if (form.clockOut) {
      const inT = new Date(`1970-01-01T${cleanTime(form.clockIn)}:00`);
      const outT = new Date(`1970-01-01T${cleanTime(form.clockOut)}:00`);
      if (outT <= inT) return toast.error("Clock Out must be after Clock In.");
    }

    const { decimal, formatted } = calculateHours(cleanTime(form.clockIn), cleanTime(form.clockOut), Number(form.breakMinutes || 0));

    const payload = {
      clock_in: `${cleanTime(form.clockIn)}:00`,
      clock_out: form.clockOut ? `${cleanTime(form.clockOut)}:00` : null,
      break_minutes: Number(form.breakMinutes || 0),
      on_shift_deliveries: Number(form.onShiftDeliveries || 0),
      off_shift_deliveries: Number(form.offShiftDeliveries || 0),
      free_deliveries: Number(form.freeDeliveries || 0),
      total_deliveries: Number(form.totalDeliveries || 0),
      delivery_fee: parseFloat(form.deliveryFee || 0),
      notes: form.notes || "",
      total_hours_decimal: decimal,
      total_hours_formatted: formatted,
      logged_at: new Date().toISOString(),
    };

    setLoading(true);
    const { error } = await supabase.from("clockins").update(payload).eq("id", editId);
    setLoading(false);

    if (error) return toast.error("DB update failed: " + error.message);

    toast.success("✅ Edit saved.");
    setEditId(null);
    fetchClockins(fromDate, toDate, filterEmployee);
  };

  // ----------------- CSV EXPORT -----------------
  const csvEscape = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const exportToCSV = () => {
    const header = [
      "Date",
      "Employee",
      "Clock In",
      "Clock Out",
      "Deliveries On Shift",
      "Deliveries Off Shift",
      "Free Deliveries",
      "Total Deliveries",
      "Delivery Fee Charged",
      "Break (min)",
      "Total Hours Worked",
      "Auto Closed",
      "Notes",
      "Logged By",
      "Logged At",
    ].join(",");

    const csvRows = rows.map((r) =>
      [
        r.date,
        r.employee,
        r.clock_in,
        r.clock_out,
        r.on_shift,
        r.off_shift,
        r.free,
        r.total,
        r.delivery_fee,
        r.break_minutes,
        r.total_hours,
        r.auto_closed,
        r.notes,
        r.logged_by,
        r.logged_at,
      ]
        .map(csvEscape)
        .join(",")
    );

    const blob = new Blob([[header, ...csvRows].join("\n")], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `clockins_${format(new Date(), "yyyyMMdd_HHmm")}.csv`);
  };

  // ----------------- COLUMNS -----------------
  const baseColumns = useMemo(
    () => [
      { field: "date", headerName: "Date", width: 110 },
      { field: "employee", headerName: "Employee", width: 150 },
      { field: "clock_in", headerName: "Clock In", width: 110 },
      { field: "clock_out", headerName: "Clock Out", width: 120 },
      { field: "on_shift", headerName: "Deliveries On Shift", width: 160 },
      { field: "off_shift", headerName: "Deliveries Off Shift", width: 160 },
      { field: "free", headerName: "Free Deliveries", width: 150 },
      { field: "total", headerName: "Total Deliveries", width: 150 },
      { field: "delivery_fee", headerName: "Delivery Fee Charged", width: 180 },
      { field: "break_minutes", headerName: "Break (min)", width: 140 },
      { field: "total_hours", headerName: "Total Hours Worked", width: 180 },
      {
        field: "auto_closed",
        headerName: "Auto Closed",
        width: 140,
        renderCell: (params) => {
          const v = params.value;
          if (v === "Yes") return <span style={{ fontWeight: 800 }}>⚠️ YES</span>;
          if (v === "No") return <span>No</span>;
          return <span>{v}</span>;
        },
      },
      { field: "notes", headerName: "Notes", width: 240 },
      { field: "logged_by", headerName: "Logged By", width: 150 },
      { field: "logged_at", headerName: "Logged At", width: 180 },
    ],
    []
  );

  const actionsColumn = useMemo(
    () => ({
      field: "actions",
      headerName: "Actions",
      width: 300,
      renderCell: (params) => {
        const allowedSnappy = canSnappyEditRow(params.row);

        return (
          <div style={{ display: "flex", gap: 8 }}>
            {allowedSnappy ? (
              <button
                className="btn-snappy"
                onClick={() => handleSnappyEdit(params.row.id)}
                title="Snappy Edit (deliveries/fee/notes only)"
              >
                Snappy Edit
              </button>
            ) : (
              <button className="btn-snappy" disabled title="You can only Snappy Edit your own entries">
                Snappy Edit
              </button>
            )}

            {isAdmin && (
              <>
                <button className="btn-edit" onClick={() => handleEdit(params.row.id)}>
                  Edit
                </button>
                <button className="btn-delete" onClick={() => handleDelete(params.row.id)}>
                  Delete
                </button>
              </>
            )}
          </div>
        );
      },
    }),
    [isAdmin, myEmployeeName, rows]
  );

  const columns = [...baseColumns, actionsColumn];

  // ----------------- UI -----------------
  return (
    <div className="main-content zreport-container">
      <div className="main-heading">🕒 Clock-in Entry</div>

      {/* Basic Info */}
      <div className="form-section">
        <div className="section-title">🧩 Basic Info</div>
        <div className="form-grid">
          <label>
            Date
            <input type="date" value={format(form.date, "yyyy-MM-dd")} disabled />
          </label>

          <label>
            Employee
            <select
              value={form.employee}
              onChange={(e) => handleEmployeeSelect(e.target.value)}
              disabled={!!editId || isSnappyEditing}
              title={isSnappyEditing ? "Exit Snappy Edit to change employee" : ""}
            >
              <option value="">Select employee</option>
              {employees.map((emp) => (
                <option key={emp} value={emp}>
                  {emp}
                </option>
              ))}
            </select>
          </label>

          <label>
            Notes
            <input
              type="text"
              value={form.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              placeholder="Optional notes..."
              disabled={false}
            />
          </label>
        </div>

        {!isAdmin && myEmployeeName && (
          <div style={{ marginTop: 8, opacity: 0.8, fontSize: 13 }}>
            Logged in as: <b>{myEmployeeName}</b> (staff)
          </div>
        )}
      </div>

      {/* Punch Clock */}
      <div className="form-section">
        <div className="section-title">⏱️ Punch Clock (UK Time)</div>

        {/* Admin edit banner */}
        {editId && (
          <div
            style={{
              background: "#fff3cd",
              border: "1px solid #ffeeba",
              padding: "10px 12px",
              borderRadius: 8,
              marginBottom: 12,
              fontWeight: 700,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div>
              ✏️ Admin Editing —
              <span style={{ marginLeft: 6 }}>{form.employee || "Unknown employee"}</span>
              <span style={{ margin: "0 6px" }}>•</span>
              {formatEditDate(form.date)}
              <span style={{ margin: "0 6px" }}>•</span>
              <span style={{ fontFamily: "monospace" }}>Row {String(editId).slice(0, 8)}</span>
            </div>

            <button
              type="button"
              className="btn-apply"
              style={{ background: "#eee", color: "#222", margin: 0 }}
              onClick={() => {
                setEditId(null);
                setForm((f) => ({ ...f, notes: "" }));
                toast.info("Edit cancelled.");
              }}
              disabled={loading}
            >
              Exit Edit
            </button>
          </div>
        )}

        {/* Snappy edit banner */}
        {isSnappyEditing && (
          <div
            style={{
              background: "#e8f4ff",
              border: "1px solid #b6e0ff",
              padding: "10px 12px",
              borderRadius: 8,
              marginBottom: 12,
              fontWeight: 700,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div>
              🟦 Snappy Editing —
              <span style={{ marginLeft: 6 }}>{form.employee || "Unknown employee"}</span>
              <span style={{ margin: "0 6px" }}>•</span>
              {formatEditDate(form.date)}
              <span style={{ margin: "0 6px" }}>•</span>
              <span style={{ fontFamily: "monospace" }}>Row {String(snappyEditId).slice(0, 8)}</span>
            </div>

            <button
              type="button"
              className="btn-apply"
              style={{ background: "#eee", color: "#222", margin: 0 }}
              onClick={() => exitSnappyEditMode()}
              disabled={loading}
            >
              Exit Snappy Edit
            </button>
          </div>
        )}

        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{formatUKDateNice(ukNow)}</div>
          <div style={{ fontSize: 34, fontWeight: 800 }}>{getUKHHmmss(ukNow)}</div>

          {expectedClockOut ? (
            <div style={{ marginTop: 6, fontWeight: 700 }}>
              Expected clock-out: {expectedClockOut}
              {form.employee ? <span style={{ marginLeft: 10 }}>({form.employee})</span> : null}
            </div>
          ) : (
            <div style={{ marginTop: 6, opacity: 0.85 }}>
              Press <b>Clock In</b> to start.
            </div>
          )}

          {/* ✅ Break status */}
          {breakStartAt && (
            <div style={{ marginTop: 8, fontWeight: 800 }}>
              ⏸️ Break active — {breakElapsed} min
              <span style={{ marginLeft: 10, fontWeight: 600, opacity: 0.85 }}>
                (started {getUKHHmmss(breakStartAt).slice(0, 5)})
              </span>
            </div>
          )}
        </div>

        <div className="form-grid">
          <label>
            Clock In
            <input type="time" value={form.clockIn || ""} onChange={(e) => handleChange("clockIn", e.target.value)} disabled={!editId} />
          </label>

          <label>
            Clock Out
            <input type="time" value={form.clockOut || ""} onChange={(e) => handleChange("clockOut", e.target.value)} disabled={!editId} />
          </label>

          <label>
            Break (min)
            <input
              type="number"
              min={0}
              step={1}
              value={form.breakMinutes ?? 0}
              onChange={(e) => handleChange("breakMinutes", Number(e.target.value))}
              disabled={!editId}
            />
          </label>

          <label>
            Total Hours Worked
            <input value={form.clockOut ? form.totalHoursFormatted : runningTotal} disabled />
          </label>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn-apply"
            onClick={openExpectedModalForClockIn}
            disabled={loading || !!activeShiftId || !!editId || isSnappyEditing}
            title={activeShiftId ? "Clock out first" : isSnappyEditing ? "Exit Snappy Edit first" : "Clock in (UK time)"}
          >
            Clock In
          </button>

          <button
            type="button"
            className="btn-apply"
            onClick={handleBreakStart}
            disabled={loading || !activeShiftId || !!breakStartAt || !!editId || isSnappyEditing}
            title="Start break"
          >
            Break Start
          </button>

          <button
            type="button"
            className="btn-apply"
            onClick={handleBreakEnd}
            disabled={loading || !breakStartAt || !!editId || isSnappyEditing}
            title="End break"
          >
            Break End
          </button>

          <button
            type="button"
            className="btn-apply"
            onClick={handleClockOutNow}
            disabled={loading || !activeShiftId || !!editId || isSnappyEditing || !!breakStartAt}
            title="Clock out (UK time)"
          >
            Clock Out
          </button>

          {editId && (
            <>
              <button type="button" className="btn-apply" onClick={handleSaveEdit} disabled={loading} title="Save edited shift">
                Save Edit
              </button>

              <button
                type="button"
                className="btn-apply"
                style={{ background: "#eee", color: "#222" }}
                onClick={() => exitEditMode()}
                disabled={loading}
              >
                Cancel Edit
              </button>
            </>
          )}
        </div>
      </div>

      {/* Deliveries */}
      <div className="form-section">
        <div className="section-title">🚚 Snappy Delivery Entry</div>
        <div className="form-grid">
          <label>
            On Shift Deliveries
            <input type="number" min={0} value={form.onShiftDeliveries} onChange={(e) => handleChange("onShiftDeliveries", Number(e.target.value))} />
          </label>
          <label>
            Off Shift Deliveries
            <input type="number" min={0} value={form.offShiftDeliveries} onChange={(e) => handleChange("offShiftDeliveries", Number(e.target.value))} />
          </label>
          <label>
            Free Deliveries
            <input type="number" min={0} value={form.freeDeliveries} onChange={(e) => handleChange("freeDeliveries", Number(e.target.value))} />
          </label>
          <label>
            Total Deliveries
            <input value={form.totalDeliveries} readOnly />
          </label>
          <label>
            Delivery Fee (£)
            <input type="number" min={0} step={0.5} value={form.deliveryFee} onChange={(e) => handleChange("deliveryFee", e.target.value)} />
          </label>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
          <button
            className="btn-apply"
            type="button"
            onClick={handleSaveDetails}
            disabled={loading || (!activeShiftId && !lastShiftId && !editId && !snappyEditId)}
          >
            Save
          </button>
          <div style={{ alignSelf: "center", opacity: 0.85 }}>Saves to the current shift (or last shift) immediately.</div>
        </div>
      </div>

      {/* Filters */}
      <div className="form-section">
        <div className="section-title">🔍 Filter</div>
        <div className="form-grid">
          <label>
            From
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} disabled={isSnappyEditing || !!editId} />
          </label>
          <label>
            To
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} disabled={isSnappyEditing || !!editId} />
          </label>
          <label>
            Employee
            <select value={filterEmployee} onChange={(e) => setFilterEmployee(e.target.value)} disabled={isSnappyEditing || !!editId}>
              <option value="">All</option>
              {employees.map((emp) => (
                <option key={emp} value={emp}>
                  {emp}
                </option>
              ))}
            </select>
          </label>

          <button className="btn-apply" type="button" onClick={() => fetchClockins(fromDate, toDate, filterEmployee)} disabled={isSnappyEditing || !!editId}>
            Apply Filter
          </button>

          <button className="btn-export" type="button" onClick={exportToCSV} disabled={isSnappyEditing || !!editId}>
            Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", marginTop: 16 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={tableLoading}
          pageSize={7}
          rowsPerPageOptions={[7, 14, 30]}
          autoHeight
          sx={{
            border: "1px solid #ccc",
            "& .MuiDataGrid-cell": { borderRight: "1px solid #ddd !important" },
            "& .MuiDataGrid-columnSeparator": { display: "block !important", backgroundColor: "#ddd !important" },
            "& .MuiDataGrid-row": { borderBottom: "1px solid #eee !important" },
          }}
        />
      </div>

      {/* Expected Clock-Out Modal */}
      {expectedModalOpen &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2147483647,
              padding: 16,
            }}
          >
            <div
              className="expected-clockout-modal"
              style={{
                width: 420,
                maxWidth: "95vw",
                background: "#fff",
                borderRadius: 14,
                padding: 20,
                boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 18, fontWeight: 800 }}>Expected clock-out time</div>
                <button
                  type="button"
                  className="btn-delete"
                  onClick={cancelExpectedModal}
                  disabled={loading}
                  style={{ padding: "6px 10px" }}
                >
                  ✕
                </button>
              </div>

              <div style={{ fontSize: 14, opacity: 0.85, marginBottom: 14 }}>
                Clock-in is automated with UK current time.
                <br />
                Enter your expected clock-out time for today.
              </div>

              <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, marginBottom: 4 }}>Clock In</div>
                  <input value={pendingClockInTime || "--:--"} disabled style={{ width: "100%" }} />
                </div>

                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, marginBottom: 4 }}>Expected Clock Out</div>
                  <input type="time" value={expectedInput} onChange={(e) => setExpectedInput(e.target.value)} style={{ width: "100%" }} />
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                <button type="button" className="btn-export" onClick={cancelExpectedModal} disabled={loading} style={{ minWidth: 140 }}>
                  Cancel
                </button>

                <button type="button" className="btn-apply" onClick={confirmClockInAndInsert} disabled={loading} style={{ minWidth: 160 }}>
                  Confirm Clock In
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}